// src/portal/portalMeasure.js
//
// The legend's figures — "#0 pad · multipolygon · 4.1 ac".
//
// ── THESE ARE COMPUTED, NOT STUBBED ────────────────────────────────────────
//
// The mock draws "4.1 ac" and "1,840 ft" and nothing in the app computed either.
// The choice was to compute them or to leave the legend to the parts that
// exist. Computed, because it is genuinely available rather than nearly:
// @turf/area and @turf/length are already dependencies, and
// plan_review_features.geom is SRID 4326 — which is exactly the input turf's
// geodesic functions take. Route 6 hands the geometry over as GeoJSON at 6
// decimal places.
//
// WHAT THESE NUMBERS ARE NOT: a survey. turf measures on a sphere, which for a
// well pad is accurate to well under a percent and is wrong to quote in a
// filing. One decimal place, as the mock draws it, is the honest precision.
import area from "@turf/area";
import length from "@turf/length";

const SQM_PER_ACRE = 4046.8564224;
const KM_PER_FOOT = 0.0003048;

const fmt = (n, digits) =>
  n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

/**
 * A human measurement for one submitted feature, or null when there is nothing
 * meaningful to say — a Point has neither area nor length.
 *
 * @param {{geometry?: object}} feature route 6's shaped feature
 * @returns {string|null} e.g. "4.1 ac", "1,840 ft"
 */
export function measureFeature(feature) {
  const g = feature?.geometry;
  if (!g || typeof g.type !== "string") return null;
  try {
    if (g.type === "Polygon" || g.type === "MultiPolygon") {
      const acres = area({ type: "Feature", geometry: g, properties: {} }) / SQM_PER_ACRE;
      if (!Number.isFinite(acres) || acres <= 0) return null;
      return `${fmt(acres, 1)} ac`;
    }
    if (g.type === "LineString" || g.type === "MultiLineString") {
      const ft = length({ type: "Feature", geometry: g, properties: {} }, { units: "kilometers" }) / KM_PER_FOOT;
      if (!Number.isFinite(ft) || ft <= 0) return null;
      return `${fmt(Math.round(ft), 0)} ft`;
    }
  } catch {
    // A malformed geometry costs the figure, not the legend row. The feature is
    // still named and still drawn; only the measurement is absent.
    return null;
  }
  return null;
}

/** "multipolygon", "multiline" — the mock's lower-case shorthand. */
export function geomWord(feature) {
  const t = feature?.geometry?.type;
  if (typeof t !== "string") return null;
  return t.toLowerCase().replace("linestring", "line");
}
